import {
  Component,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ChangeDetectorRef
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-json-builder',
  templateUrl: './json-builder.component.html',
  styleUrls: ['./json-builder.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => JsonBuilderComponent),
      multi: true,
    },
  ]
})
export class JsonBuilderComponent implements ControlValueAccessor, OnChanges, OnDestroy {
  @Input() label: string = 'Permissions';

  activeTab: 'builder' | 'json' = 'builder';
  entries: Array<{ key: string; type: string; value: any; fields?: Array<{ value: string; }>; }> = [];
  disabled = false;
  private lastEmittedValue: string = '';
  private destroy$ = new Subject<void>();
  private valueChange$ = new Subject<void>();

  private onChange = (value: any) => { };
  private onTouched = () => { };

  constructor(private cdr: ChangeDetectorRef) {
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && changes['data'].currentValue) {
      this.loadEntriesFromData(changes['data'].currentValue);
    }
  }

  writeValue(value: any): void {
    if (value === this.lastEmittedValue) {
      return;
    }

    try {
      const parsedData = typeof value === 'string' ? JSON.parse(value) : value;
      this.loadEntriesFromData(parsedData);
      this.lastEmittedValue = JSON.stringify(parsedData, null, 2);
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error parsing value:', error);
      this.loadEntriesFromData({});
      this.lastEmittedValue = '{}';
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  private emitChange(): void {
    const jsonObject = this.buildJsonObjectFromEntries();
    const jsonString = JSON.stringify(jsonObject, null, 2);

    if (jsonString !== this.lastEmittedValue) {
      this.lastEmittedValue = jsonString;
      this.onChange(jsonString);
      this.onTouched();
    }
  }

  handleInputChange(value: any): void {
    const jsonObject = this.buildJsonObjectFromEntries();
    const jsonString = JSON.stringify(jsonObject, null, 2);

    if (jsonString !== this.lastEmittedValue) {
      this.lastEmittedValue = jsonString;
      this.onChange(jsonString);
      this.onTouched();
    }
  }

  get formattedJson(): string {
    try {
      const jsonObject = this.buildJsonObjectFromEntries();
      return JSON.stringify(jsonObject, null, 2);
    } catch (error) {
      return '{}';
    }
  }

  switchTab(tab: 'builder' | 'json'): void {
    this.activeTab = tab;
  }

  addEntry(): void {
    this.entries.push({ key: '', type: 'string', value: '' });
    this.emitChange();
  }

  removeEntry(index: number): void {
    this.entries.splice(index, 1);
    this.emitChange();
  }

  addArrayField(entry: any): void {
    if (!entry.fields) {
      entry.fields = [];
    }
    entry.fields.push({ value: '' });
    this.emitChange();
  }

  removeArrayField(fields: any[], index: number): void {
    fields.splice(index, 1);
    this.emitChange();
  }

  handleJsonChange(event: any): void {
    try {
      const jsonText = event.target.value;
      if (jsonText === this.lastEmittedValue) {
        return;
      }

      const parsedJson = JSON.parse(jsonText);
      this.loadEntriesFromData(parsedJson);
      this.lastEmittedValue = jsonText;
      this.onChange(jsonText);
      this.onTouched();
    } catch (error) {
      console.log('Invalid JSON format');
    }
  }

  private loadEntriesFromData(data: any): void {
    if (!data || typeof data !== 'object') {
      this.entries = [];
      return;
    }

    try {
      this.entries = Object.keys(data).map(key => {
        const value = data[key];
        if (Array.isArray(value)) {
          return {
            key,
            type: 'array',
            value: '',
            fields: value.map(item => ({ value: String(item) }))
          };
        } else if (typeof value === 'boolean') {
          return { key, type: 'boolean', value: value.toString() };
        } else if (typeof value === 'number') {
          return { key, type: 'number', value: value };
        } else {
          return { key, type: 'string', value: String(value) };
        }
      });
    } catch (error) {
      console.error('Error loading entries:', error);
      this.entries = [];
    }
  }

  private buildJsonObjectFromEntries(): any {
    const jsonObject: any = {};
    this.entries.forEach(entry => {
      if (entry.key && entry.key.trim() !== '') {
        if (entry.type === 'number') {
          jsonObject[entry.key] = Number(entry.value);
        } else if (entry.type === 'boolean') {
          jsonObject[entry.key] = entry.value === 'true';
        } else if (entry.type === 'array') {
          jsonObject[entry.key] = entry.fields?.map(field => field.value)
            .filter(value => value && value.trim() !== '') || [];
        } else {
          jsonObject[entry.key] = entry.value;
        }
      }
    });
    return jsonObject;
  }
}
